<template>
  <main
    class="cast-page"
    :class="{ 'controls-visible': controlsVisible }"
    @mousemove="showControls"
    @click="showControls"
  >
    <section v-if="phase === 'idle'" class="start-screen">
      <div class="brand-lockup">
        <span class="brand-mark">LOOP</span>
        <span>快乐鹿视频课程</span>
      </div>
      <h1>电脑投屏接收端</h1>
      <p>将电脑连接到投影仪、电视或比赛大屏后进入接收模式。</p>
      <button class="primary-button" :disabled="busy" @click.stop="startReceiver">
        <span class="button-icon">⛶</span>
        {{ busy ? '正在连接...' : '进入接收模式并全屏' }}
      </button>
      <p v-if="errorMessage" class="inline-error">{{ errorMessage }}</p>
    </section>

    <section v-else-if="phase === 'waiting'" class="pair-screen">
      <div class="pair-heading">
        <span class="status-dot"></span>
        等待手机连接
      </div>
      <div class="pair-layout">
        <div class="pair-code-block">
          <p>在 LOOP APP 播放页点击投屏，输入投屏码</p>
          <div class="pair-code" aria-label="投屏码">{{ formattedPairCode }}</div>
          <span class="countdown">{{ countdownText }} 后失效</span>
        </div>
        <div class="qr-panel">
          <div ref="qrCode" class="qr-code"></div>
          <span>也可使用 APP 扫码连接</span>
        </div>
      </div>
      <button class="text-button" @click.stop="restartReceiver">重新生成投屏码</button>
    </section>

    <section v-else class="receiver-screen">
      <div id="cast-player" class="player-host"></div>
      <div
        v-if="watermarkText && playerReady && (phase === 'playing' || phase === 'paused')"
        class="dynamic-watermark"
        :style="watermarkStyle"
      >{{ watermarkText }}</div>
      <div v-if="phase !== 'playing' && phase !== 'paused'" class="status-layer">
        <div class="status-symbol">{{ statusSymbol }}</div>
        <h2>{{ statusTitle }}</h2>
        <p>{{ statusMessage }}</p>
        <button v-if="phase === 'error' || phase === 'expired'" class="secondary-button" @click.stop="restartReceiver">
          重新连接
        </button>
      </div>

      <header v-show="controlsVisible" class="top-controls">
        <div class="course-copy">
          <span>{{ seriesTitle || '快乐鹿视频课程' }}</span>
          <strong>{{ lessonTitle || '准备播放' }}</strong>
        </div>
        <div class="connection-state">
          <span class="status-dot"></span>
          手机已连接
        </div>
      </header>

      <div v-show="controlsVisible && playerReady" class="center-control">
        <button class="round-control play-control" :title="isPlaying ? '暂停' : '播放'" @click.stop="togglePlayback">
          {{ isPlaying ? 'Ⅱ' : '▶' }}
        </button>
      </div>

      <footer v-show="controlsVisible" class="bottom-controls">
        <div class="timeline-row">
          <span>{{ formatTime(position) }}</span>
          <div class="timeline" role="progressbar" :aria-valuenow="Math.floor(position)" aria-valuemin="0" :aria-valuemax="Math.max(Math.floor(duration), 1)">
            <span :style="{ width: progressPercent + '%' }"></span>
          </div>
          <span>{{ formatTime(duration) }}</span>
        </div>
        <div class="emergency-controls">
          <button class="icon-button" :title="isPlaying ? '暂停' : '播放'" @click.stop="togglePlayback">
            {{ isPlaying ? 'Ⅱ' : '▶' }}
          </button>
          <label class="volume-control" title="音量">
            <span>🔊</span>
            <input type="range" min="0" max="100" :value="volume" @input="changeVolume">
          </label>
          <button class="icon-button" title="全屏" @click.stop="requestFullscreen">⛶</button>
          <button class="disconnect-button" @click.stop="disconnect">断开投屏</button>
        </div>
      </footer>
    </section>
  </main>
</template>

<script>
import QRCode from 'qrcodejs2'
import {
  clearReceiverSession,
  closeReceiver,
  createReceiver,
  loadReceiverSession,
  pollReceiver,
  receiverErrorMessage,
  reportReceiver,
  saveReceiverSession,
} from '../services/castReceiver'

const PLAYER_VERSION = '2.38.0'
const PLAYER_SCRIPT = `https://g.alicdn.com/apsara-media-box/imp-web-player/${PLAYER_VERSION}/aliplayer-min.js`
const PLAYER_STYLE = `https://g.alicdn.com/apsara-media-box/imp-web-player/${PLAYER_VERSION}/skins/default/aliplayer-min.css`

export default {
  name: 'CastReceiver',
  data() {
    return {
      phase: 'idle',
      busy: false,
      pairCode: '',
      expiresAt: 0,
      nowSeconds: Math.floor(Date.now() / 1000),
      token: '',
      sessionId: '',
      lastAppliedVersion: 0,
      pollTimer: null,
      reportTimer: null,
      countdownTimer: null,
      controlsTimer: null,
      watermarkTimer: null,
      controlsVisible: true,
      pollInFlight: false,
      reportInFlight: false,
      player: null,
      playerReady: false,
      lessonId: 0,
      lessonTitle: '',
      seriesTitle: '',
      position: 0,
      duration: 0,
      volume: 100,
      speed: 1,
      isPlaying: false,
      errorMessage: '',
      watermarkText: '',
      watermarkCode: '',
      watermarkPosition: 0,
      securityMode: 'standard',
    }
  },
  computed: {
    formattedPairCode() {
      const code = String(this.pairCode || '')
      return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code
    },
    countdownText() {
      const seconds = Math.max(0, this.expiresAt - this.nowSeconds)
      return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
    },
    statusSymbol() {
      return this.phase === 'buffering' ? '···' : this.phase === 'disconnected' ? '⌁' : '!'
    },
    statusTitle() {
      const titles = {
        connected: '手机已连接', buffering: '正在缓冲', disconnected: '等待手机恢复连接',
        error: '播放失败', expired: '投屏会话已结束',
      }
      return titles[this.phase] || '正在准备播放'
    },
    statusMessage() {
      if (this.errorMessage) return this.errorMessage
      const messages = {
        connected: '正在获取课程播放凭证', buffering: '网络波动，正在恢复播放',
        disconnected: '电脑将继续播放，手机恢复后可重新接管',
        expired: '请在电脑端重新生成投屏码',
      }
      return messages[this.phase] || ''
    },
    progressPercent() {
      if (!this.duration) return 0
      return Math.max(0, Math.min(100, (this.position / this.duration) * 100))
    },
    watermarkStyle() {
      const positions = [
        { left: '44%', top: '32%' },
        { left: '55%', top: '49%' },
        { left: '46%', top: '65%' },
      ]
      return positions[this.watermarkPosition] || positions[0]
    },
  },
  mounted() {
    document.title = 'LOOP 电脑投屏'
    const saved = loadReceiverSession()
    if (saved.token && saved.sessionId) {
      this.token = saved.token
      this.sessionId = saved.sessionId
      this.pairCode = saved.pairCode
      this.expiresAt = saved.expiresAt
      this.phase = saved.pairCode && saved.expiresAt > this.nowSeconds ? 'waiting' : 'connected'
      if (this.phase === 'waiting') {
        this.$nextTick(() => this.renderQrCode(`loopcast://pair?v=1&code=${saved.pairCode}`))
      }
      this.beginPolling()
    }
    this.countdownTimer = window.setInterval(() => {
      this.nowSeconds = Math.floor(Date.now() / 1000)
      if (this.phase === 'waiting' && this.expiresAt > 0 && this.nowSeconds >= this.expiresAt) {
        this.expireSession('投屏码已失效')
      }
    }, 1000)
    document.addEventListener('fullscreenchange', this.showControls)
  },
  beforeDestroy() {
    this.stopTimers()
    this.destroyPlayer()
    document.removeEventListener('fullscreenchange', this.showControls)
  },
  methods: {
    async startReceiver() {
      if (this.busy) return
      this.busy = true
      this.errorMessage = ''
      try {
        await this.requestFullscreen()
        const payload = await createReceiver(this.receiverName())
        if (!payload || !/^\d{6}$/.test(String(payload.pair_code || '')) || !payload.receiver_token || !payload.session_id) {
          throw new Error('invalid cast receiver response')
        }
        this.token = payload.receiver_token
        this.sessionId = payload.session_id
        this.pairCode = payload.pair_code
        this.expiresAt = payload.expires_at
        this.lastAppliedVersion = 0
        saveReceiverSession(this.token, this.sessionId, this.pairCode, this.expiresAt)
        this.phase = 'waiting'
        const qrPayload = payload.qr_payload || `loopcast://pair?v=1&code=${payload.pair_code}`
        this.$nextTick(() => this.renderQrCode(qrPayload))
        this.beginPolling(payload.poll_interval_ms || 800)
      } catch (error) {
        this.phase = 'idle'
        this.errorMessage = receiverErrorMessage(error, '接收服务连接失败，请检查网络后重试')
      } finally {
        this.busy = false
      }
    },
    async restartReceiver() {
      const oldToken = this.token
      this.resetReceiver(false)
      if (oldToken) {
        try { await closeReceiver(oldToken) } catch (_) { /* expired sessions need no cleanup */ }
      }
      await this.startReceiver()
    },
    receiverName() {
      const platform = navigator.userAgentData && navigator.userAgentData.platform
      return `${platform || navigator.platform || 'Windows'} 浏览器`
    },
    renderQrCode(payload) {
      if (!this.$refs.qrCode) return
      this.$refs.qrCode.innerHTML = ''
      new QRCode(this.$refs.qrCode, {
        text: payload,
        width: 220,
        height: 220,
        colorDark: '#17181f',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.M,
      })
    },
    beginPolling(interval = 800) {
      window.clearInterval(this.pollTimer)
      this.pollOnce()
      this.pollTimer = window.setInterval(this.pollOnce, interval)
      window.clearInterval(this.reportTimer)
      this.reportTimer = window.setInterval(this.reportState, 2000)
    },
    async pollOnce() {
      if (!this.token || this.pollInFlight) return
      this.pollInFlight = true
      try {
        const payload = await pollReceiver(this.token, this.lastAppliedVersion, !this.playerReady)
        const session = payload.session || {}
        this.syncSession(session)
        if (payload.command && payload.command.version > this.lastAppliedVersion) {
          await this.applyCommand(payload.command)
          this.lastAppliedVersion = payload.command.version
        }
      } catch (error) {
        const status = error && error.response && error.response.status
        if (status === 401) {
          this.expireSession(receiverErrorMessage(error, '投屏会话已失效'))
        } else if (this.phase !== 'error') {
          this.phase = this.playerReady ? (this.isPlaying ? 'playing' : 'paused') : 'disconnected'
          this.errorMessage = '接收服务暂时断开，正在自动重连'
        }
      } finally {
        this.pollInFlight = false
      }
    },
    syncSession(session) {
      this.lessonId = session.lesson_id || this.lessonId
      this.lessonTitle = session.lesson_title || this.lessonTitle
      this.seriesTitle = session.series_title || this.seriesTitle
      if (!this.playerReady) {
        this.position = session.position_seconds || this.position
        this.duration = session.duration_seconds || this.duration
      }
      if (session.status === 'waiting') {
        this.phase = 'waiting'
      } else if (session.status === 'disconnected') {
        this.phase = 'disconnected'
      } else if (this.phase === 'waiting') {
        saveReceiverSession(this.token, this.sessionId)
        this.phase = 'connected'
      }
      if (session.error_message) this.errorMessage = session.error_message
    },
    async applyCommand(command) {
      switch (command.type) {
        case 'LOAD':
          await this.loadVideo(command)
          break
        case 'PLAY':
          this.player && this.player.play()
          break
        case 'PAUSE':
          this.player && this.player.pause()
          break
        case 'SEEK':
          this.player && this.player.seek(command.position_seconds || 0)
          break
        case 'SET_VOLUME':
          this.setPlayerVolume(command.volume_percent)
          break
        case 'SET_SPEED':
          this.speed = command.speed || 1
          this.player && this.player.setSpeed && this.player.setSpeed(this.speed)
          break
        case 'STOP':
          this.destroyPlayer()
          this.clearWatermark()
          this.expireSession(this.errorMessage || '手机已结束投屏')
          break
        default:
          throw new Error('unsupported cast command')
      }
    },
    async loadVideo(command) {
      if (!command.credential || !command.credential.video_id || !command.credential.play_auth) {
        throw new Error('播放凭证无效')
      }
      const protectedLoad = Object.prototype.hasOwnProperty.call(command, 'security_mode')
      const watermark = command.watermark || {}
      if (protectedLoad && (!watermark.display_text || !watermark.code || Number(watermark.expires_at || 0) <= Math.floor(Date.now() / 1000))) {
        this.phase = 'error'
        this.errorMessage = '受保护课程缺少有效播放水印，已拒绝无水印播放。请在手机端重新发起投屏。'
        throw new Error(this.errorMessage)
      }
      this.phase = 'buffering'
      this.errorMessage = ''
      this.lessonId = command.lesson_id
      this.lessonTitle = command.lesson_title || ''
      this.seriesTitle = command.series_title || ''
      this.position = command.position_seconds || 0
      this.volume = command.volume_percent == null ? this.volume : command.volume_percent
      this.speed = command.speed || this.speed
      this.watermarkText = String(watermark.display_text || '')
      this.watermarkCode = String(watermark.code || '')
      this.securityMode = command.security_mode || 'standard'
      this.startWatermarkTimer()
      await this.ensurePlayerSdk()
      this.destroyPlayer()
      await new Promise((resolve, reject) => {
        let settled = false
        const finish = callback => value => {
          if (settled) return
          settled = true
          callback(value)
        }
        const privateEncrypted = this.securityMode === 'aliyun_private_hls'
        this.player = new window.Aliplayer({
          id: 'cast-player',
          vid: command.credential.video_id,
          playauth: command.credential.play_auth,
          region: command.credential.region || 'cn-shanghai',
          autoplay: command.autoplay !== false,
          width: '100%',
          height: '100%',
          useH5Prism: true,
          playsinline: true,
          skinLayout: false,
          ...(privateEncrypted ? {
            encryptType: 1,
            playConfig: { EncryptType: 'AliyunVoDEncryption' },
          } : {}),
          license: {
            domain: process.env.VUE_APP_ALIYUN_PLAYER_LICENSE_DOMAIN || 'loopsports.cn',
            key: process.env.VUE_APP_ALIYUN_PLAYER_LICENSE_KEY || '',
          },
        }, finish(() => {
          this.playerReady = true
          this.setPlayerVolume(this.volume)
          this.player.setSpeed && this.player.setSpeed(this.speed)
          if (this.position > 0) this.player.seek(this.position)
          if (command.autoplay === false) {
            this.player.pause()
            this.isPlaying = false
            this.phase = 'paused'
          } else {
            this.phase = 'playing'
          }
          this.showControls()
          resolve()
        }))
        this.bindPlayerEvents(finish(reject))
      })
    },
    bindPlayerEvents(rejectLoad) {
      if (!this.player || !this.player.on) return
      this.player.on('play', () => { this.isPlaying = true; this.phase = 'playing'; this.errorMessage = '' })
      this.player.on('pause', () => { this.isPlaying = false; this.phase = 'paused' })
      this.player.on('playing', () => { this.isPlaying = true; this.phase = 'playing'; this.errorMessage = '' })
      this.player.on('waiting', () => { this.phase = 'buffering' })
      this.player.on('canplay', () => { this.phase = this.isPlaying ? 'playing' : 'paused' })
      this.player.on('timeupdate', () => {
        this.position = this.player.getCurrentTime ? this.player.getCurrentTime() || 0 : this.position
        this.duration = this.player.getDuration ? this.player.getDuration() || 0 : this.duration
      })
      this.player.on('error', event => {
        const message = event && event.paramData && event.paramData.error_msg
        this.errorMessage = message || '视频播放失败，请在手机端重试或切换课时'
        this.phase = 'error'
        rejectLoad(new Error(this.errorMessage))
      })
    },
    ensurePlayerSdk() {
      if (window.Aliplayer) return Promise.resolve()
      if (!document.querySelector(`link[href="${PLAYER_STYLE}"]`)) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = PLAYER_STYLE
        document.head.appendChild(link)
      }
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${PLAYER_SCRIPT}"]`)
        if (existing) {
          existing.addEventListener('load', resolve, { once: true })
          existing.addEventListener('error', reject, { once: true })
          return
        }
        const script = document.createElement('script')
        script.src = PLAYER_SCRIPT
        script.charset = 'utf-8'
        script.onload = resolve
        script.onerror = () => reject(new Error('播放器组件加载失败'))
        document.head.appendChild(script)
      })
    },
    reportState() {
      if (!this.token || !this.lessonId || this.reportInFlight) return
      this.reportInFlight = true
      reportReceiver(this.token, this, this.lastAppliedVersion)
        .catch(() => {})
        .finally(() => { this.reportInFlight = false })
    },
    togglePlayback() {
      if (!this.playerReady || !this.player) return
      if (this.isPlaying) this.player.pause()
      else this.player.play()
      this.showControls()
    },
    changeVolume(event) {
      this.setPlayerVolume(Number(event.target.value))
    },
    setPlayerVolume(value) {
      this.volume = Math.max(0, Math.min(100, Number(value) || 0))
      this.player && this.player.setVolume && this.player.setVolume(this.volume / 100)
    },
    async disconnect() {
      const token = this.token
      this.reportState()
      this.destroyPlayer()
      this.resetReceiver(false)
      if (token) {
        try { await closeReceiver(token) } catch (_) { /* local state is already closed */ }
      }
      this.phase = 'idle'
    },
    expireSession(message) {
      this.errorMessage = message
      this.phase = 'expired'
      clearReceiverSession()
      this.token = ''
      window.clearInterval(this.pollTimer)
      window.clearInterval(this.reportTimer)
    },
    resetReceiver(resetPhase = true) {
      this.stopTimers(false)
      this.destroyPlayer()
      clearReceiverSession()
      this.token = ''
      this.sessionId = ''
      this.pairCode = ''
      this.expiresAt = 0
      this.lastAppliedVersion = 0
      this.lessonId = 0
      this.lessonTitle = ''
      this.seriesTitle = ''
      this.position = 0
      this.duration = 0
      this.errorMessage = ''
      this.clearWatermark()
      if (resetPhase) this.phase = 'idle'
    },
    stopTimers(includeCountdown = true) {
      window.clearInterval(this.pollTimer)
      window.clearInterval(this.reportTimer)
      window.clearTimeout(this.controlsTimer)
      window.clearInterval(this.watermarkTimer)
      if (includeCountdown) window.clearInterval(this.countdownTimer)
    },
    destroyPlayer() {
      if (this.player && this.player.dispose) this.player.dispose()
      this.player = null
      this.playerReady = false
      this.isPlaying = false
      const host = document.getElementById('cast-player')
      if (host) host.innerHTML = ''
    },
    startWatermarkTimer() {
      window.clearInterval(this.watermarkTimer)
      this.watermarkPosition = 0
      if (!this.watermarkText) return
      this.watermarkTimer = window.setInterval(() => {
        this.watermarkPosition = (this.watermarkPosition + 1) % 3
      }, 10000)
    },
    clearWatermark() {
      window.clearInterval(this.watermarkTimer)
      this.watermarkTimer = null
      this.watermarkText = ''
      this.watermarkCode = ''
      this.watermarkPosition = 0
      this.securityMode = 'standard'
    },
    requestFullscreen() {
      const element = this.$el
      if (!document.fullscreenElement && element && element.requestFullscreen) {
        return element.requestFullscreen().catch(() => {})
      }
      return Promise.resolve()
    },
    showControls() {
      this.controlsVisible = true
      window.clearTimeout(this.controlsTimer)
      if (this.phase === 'playing') {
        this.controlsTimer = window.setTimeout(() => { this.controlsVisible = false }, 3000)
      }
    },
    formatTime(value) {
      const seconds = Math.max(0, Math.floor(value || 0))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const rest = seconds % 60
      return hours > 0
        ? `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
        : `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
    },
  },
}
</script>

<style scoped>
.cast-page {
  --background: #111218;
  --panel: #22232c;
  --panel-soft: #2d2e38;
  --primary: #ffd500;
  --text: #f7f7f8;
  --muted: #a9aab3;
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: var(--background);
  color: var(--text);
  font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;
  letter-spacing: 0;
}

button, input { font: inherit; }
button { min-height: 44px; cursor: pointer; }
button:disabled { cursor: wait; opacity: .65; }

.start-screen, .pair-screen {
  width: min(980px, calc(100vw - 80px));
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.brand-lockup {
  position: absolute;
  top: 38px;
  left: 48px;
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--muted);
  font-size: 17px;
}
.brand-mark { color: var(--primary); font-size: 24px; font-weight: 800; }
.start-screen h1 { margin: 0 0 18px; font-size: 56px; font-weight: 700; }
.start-screen p { margin: 0 0 38px; color: var(--muted); font-size: 20px; }
.primary-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 0 28px;
  border: 0;
  border-radius: 6px;
  background: var(--primary);
  color: #17181f;
  font-size: 20px;
  font-weight: 700;
}
.button-icon { font-size: 27px; line-height: 1; }
.inline-error { margin-top: 22px !important; color: #ff7777 !important; }

.pair-heading { margin-bottom: 52px; color: var(--muted); font-size: 20px; }
.status-dot { display: inline-block; width: 10px; height: 10px; margin-right: 10px; border-radius: 50%; background: #2fd58b; }
.pair-layout { width: 100%; display: grid; grid-template-columns: 1fr 320px; align-items: center; gap: 80px; text-align: left; }
.pair-code-block p { margin: 0 0 24px; color: var(--muted); font-size: 19px; }
.pair-code { color: var(--primary); font-size: 110px; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }
.countdown { display: block; margin-top: 28px; color: var(--muted); font-size: 18px; }
.qr-panel { padding: 28px; border: 1px solid #3c3d47; border-radius: 8px; background: #fff; color: #34353e; text-align: center; }
.qr-code { width: 220px; height: 220px; margin: 0 auto 18px; }
.qr-code >>> img, .qr-code >>> canvas { max-width: 100%; }
.text-button { margin-top: 46px; border: 0; background: transparent; color: var(--primary); font-size: 17px; }

.receiver-screen, .player-host { position: absolute; inset: 0; background: #000; }
.player-host >>> video { width: 100% !important; height: 100% !important; object-fit: contain !important; background: #000; }
.dynamic-watermark {
  position: absolute;
  z-index: 6;
  width: min(520px, 54vw);
  color: rgba(255, 255, 255, .22);
  font-size: clamp(15px, 1.55vw, 28px);
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, .65);
  pointer-events: none;
  transform: translate(-50%, -50%) rotate(-5deg);
  transition: left .42s ease, top .42s ease;
}
.status-layer { position: absolute; inset: 0; z-index: 4; display: flex; flex-direction: column; align-items: center; justify-content: center; background: rgba(17, 18, 24, .88); text-align: center; }
.status-symbol { color: var(--primary); font-size: 64px; font-weight: 700; }
.status-layer h2 { margin: 20px 0 10px; font-size: 32px; }
.status-layer p { margin: 0; color: var(--muted); font-size: 18px; }
.secondary-button { margin-top: 28px; padding: 0 24px; border: 1px solid var(--primary); border-radius: 5px; background: transparent; color: var(--primary); }
.top-controls, .bottom-controls, .center-control { position: absolute; z-index: 8; transition: opacity .18s ease; }
.top-controls { top: 0; right: 0; left: 0; display: flex; justify-content: space-between; align-items: flex-start; padding: 28px 34px 70px; background: linear-gradient(to bottom, rgba(0, 0, 0, .78), transparent); }
.course-copy { display: flex; flex-direction: column; gap: 7px; }
.course-copy span { color: #d0d1d6; font-size: 15px; }
.course-copy strong { max-width: min(70vw, 900px); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 23px; }
.connection-state { padding-top: 8px; color: #d7d8dc; font-size: 15px; }
.center-control { top: 50%; left: 50%; transform: translate(-50%, -50%); }
.round-control { width: 78px; height: 78px; min-height: 78px; border: 0; border-radius: 50%; background: rgba(255, 213, 0, .94); color: #17181f; font-size: 32px; font-weight: 800; }
.bottom-controls { right: 0; bottom: 0; left: 0; padding: 70px 34px 28px; background: linear-gradient(to top, rgba(0, 0, 0, .82), transparent); }
.timeline-row { display: grid; grid-template-columns: 60px 1fr 60px; align-items: center; gap: 14px; font-variant-numeric: tabular-nums; }
.volume-control input { accent-color: var(--primary); }
.timeline { position: relative; width: 100%; height: 5px; overflow: hidden; border-radius: 3px; background: rgba(255, 255, 255, .3); }
.timeline span { position: absolute; inset: 0 auto 0 0; background: var(--primary); }
.emergency-controls { display: flex; align-items: center; gap: 16px; margin-top: 16px; }
.icon-button { width: 46px; min-height: 46px; border: 0; border-radius: 50%; background: rgba(45, 46, 56, .9); color: white; font-size: 20px; }
.volume-control { display: flex; align-items: center; gap: 10px; }
.volume-control input { width: 150px; }
.disconnect-button { margin-left: auto; padding: 0 18px; border: 1px solid #72737b; border-radius: 5px; background: rgba(34, 35, 44, .92); color: white; }

@media (min-width: 2560px) and (min-height: 1400px) {
  .brand-lockup { top: 60px; left: 72px; gap: 20px; font-size: 24px; }
  .brand-mark { font-size: 34px; }
  .start-screen h1 { font-size: 78px; }
  .start-screen p { margin-bottom: 52px; font-size: 28px; }
  .primary-button { min-height: 64px; padding: 0 40px; font-size: 28px; }
  .pair-screen { width: min(1400px, calc(100vw - 160px)); }
  .pair-heading { margin-bottom: 72px; font-size: 28px; }
  .pair-layout { grid-template-columns: 1fr 440px; gap: 120px; }
  .pair-code-block p { font-size: 27px; }
  .pair-code { font-size: 156px; }
  .countdown, .text-button { font-size: 25px; }
  .qr-panel { padding: 38px; font-size: 22px; }
  .qr-code { width: 310px; height: 310px; }
  .top-controls { padding: 48px 60px 110px; }
  .course-copy { gap: 10px; }
  .course-copy span, .connection-state { font-size: 23px; }
  .course-copy strong { max-width: min(70vw, 1300px); font-size: 34px; }
  .round-control { width: 112px; height: 112px; min-height: 112px; font-size: 46px; }
  .bottom-controls { padding: 110px 60px 48px; font-size: 22px; }
  .timeline-row { grid-template-columns: 90px 1fr 90px; gap: 20px; }
  .timeline { height: 8px; }
  .emergency-controls { gap: 24px; margin-top: 24px; }
  .icon-button { width: 64px; min-height: 64px; font-size: 28px; }
  .volume-control input { width: 230px; }
  .disconnect-button { min-height: 58px; padding: 0 26px; }
}

@media (max-width: 900px) {
  .start-screen h1 { font-size: 38px; }
  .start-screen p { font-size: 16px; }
  .pair-layout { grid-template-columns: 1fr; gap: 34px; text-align: center; }
  .qr-panel { width: 260px; margin: 0 auto; }
  .pair-code { font-size: 68px; }
  .pair-heading { margin-bottom: 28px; }
}
</style>
